import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Space, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { getBelegCheck, getComplianceMap } from "./api";
import { LEVEL_COLORS, formatEuro, levelLabels, warningText } from "./format";
import "./subcontractors.css";

/** Traffic light of a subcontractor's documents: red / amber / green. */
const ComplianceTag = ({ compliance }) => {
  const { t } = useTranslation("dashboard");
  if (!compliance) return null;
  return (
    <Tag color={LEVEL_COLORS[compliance.level]} className="sc-light">
      <span className={`sc-dot sc-dot-${compliance.level}`} aria-hidden="true" />
      {levelLabels(t)[compliance.level]}
    </Tag>
  );
};

// One request per page view, however many suppliers show a badge
let mapRequest = null;
let mapLoadedAt = 0;
const loadMap = () => {
  if (!mapRequest || Date.now() - mapLoadedAt > 60000) {
    mapLoadedAt = Date.now();
    mapRequest = getComplianceMap()
      .then(res => res.data)
      .catch(() => ({ data: {}, canOpen: false }));
  }
  return mapRequest;
};

/**
 * "Documents complete?" badge next to a supplier wherever suppliers are listed (accounting
 * suppliers, Belege). Renders nothing for suppliers that are no subcontractors, or when the
 * user may see neither subcontractors nor the books.
 */
export const SupplierComplianceBadge = ({ supplierId }) => {
  const { t } = useTranslation("dashboard");
  const [entry, setEntry] = useState(null);
  const [canOpen, setCanOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (supplierId)
      loadMap().then(result => {
        if (!alive) return;
        setEntry(result.data?.[String(supplierId)] || null);
        setCanOpen(Boolean(result.canOpen));
      });
    return () => {
      alive = false;
    };
  }, [supplierId]);

  if (!entry) return null;
  const tag = (
    <Tooltip title={t("Subcontractor documents")}>
      <Tag color={LEVEL_COLORS[entry.level]} className="sc-light">
        <span className={`sc-dot sc-dot-${entry.level}`} aria-hidden="true" />
        {entry.complete ? t("Documents complete") : t("Documents incomplete")}
      </Tag>
    </Tooltip>
  );
  return canOpen ? <Link to={`/user/subcontractors/${entry.subcontractor}`}>{tag}</Link> : tag;
};

/** § 48 / § 13b warnings for an incoming invoice of a subcontractor (Beleg drawer). */
export const BelegSubcontractorAlert = ({ belegId }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [check, setCheck] = useState(null);

  useEffect(() => {
    let alive = true;
    setCheck(null);
    if (belegId)
      getBelegCheck(belegId)
        .then(res => alive && setCheck(res.data.data))
        .catch(() => null);
    return () => {
      alive = false;
    };
  }, [belegId]);

  if (!check || !check.warnings.length) return null;
  return (
    <Alert
      className="mb-3"
      type={check.withholding.withhold ? "error" : "warning"}
      showIcon
      message={
        <Space size={4} wrap>
          {t("Subcontractor")}: {check.subcontractor.name}
          <ComplianceTag compliance={{ level: check.level }} />
        </Space>
      }
      description={
        <ul className="sc-warning-list">
          {check.warnings.map(code => (
            <li key={code}>{warningText(t, code)}</li>
          ))}
          {check.withholding.withhold && (
            <li>
              {t("Bauabzugsteuer to withhold: {{amount}}", {
                amount: formatEuro(check.withholding.withheld, i18n.language)
              })}
            </li>
          )}
        </ul>
      }
    />
  );
};

export default ComplianceTag;
